import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import { apiClient } from "@/lib/api";
import { cn } from "@/lib/utils";
import type { ScopeKind, Uuid } from "@tilt/admin-sdk";

/**
 * Hierarchical name-based scope picker - Partner → Organization →
 * Location. Replaces the old "paste a UUID" inputs that were the
 * primary complaint on the Grant POS access modal.
 *
 * The user picks a partner first; the organization dropdown narrows
 * to that partner; the location dropdown narrows to that org. The
 * exposed value is `{ kind, id }` - an empty `id` means the user
 * hasn't picked anything yet at the chosen depth, so callers can keep
 * the submit button disabled until the picker settles.
 *
 * Constraints:
 *   - `allowedKinds` lets a caller forbid kinds the surrounding
 *     workflow doesn't support (e.g. POS pool with an org-anchor
 *     can't grant at a partner scope). Default: all three.
 *   - `boundPartnerPath` (optional) constrains the partner picker to
 *     the named subtree. The string is a partner ltree path like
 *     "platform.finterra"; the picker shows only partners whose path
 *     equals it or starts with it + ".". Useful when you want to
 *     surface only what's reachable under a specific anchor.
 *   - `boundOrganizationId` (optional) hard-pins the organization to
 *     the named id (so a POS user anchored at one org can't pick a
 *     sibling). Hides the partner row entirely.
 *
 * Out of scope here: server-side authz (the API does its own scope
 * check on every grant - this picker just keeps the user from
 * proposing an unreachable scope).
 */
export interface WorkplacePickerValue {
  kind: ScopeKind | null;
  id: Uuid;
  /** Display labels - purely for badge rendering in the parent. */
  labels: {
    partner: string | null;
    organization: string | null;
    location: string | null;
  };
}

export interface WorkplacePickerProps {
  value: WorkplacePickerValue;
  onChange: (next: WorkplacePickerValue) => void;
  allowedKinds?: readonly ScopeKind[];
  boundPartnerPath?: string;
  /** Pin the picker to one organization. The org row renders as a
   *  read-only badge showing ``boundOrganizationName`` (or the id if
   *  the name is omitted) - operators can't pick a different org.
   *
   *  Hierarchy reminder: organizations DON'T contain other
   *  organizations. So when the parent has an org-anchored POS user
   *  and passes the anchor here, ``Organization`` depth means "use
   *  this anchor org" (no dropdown) and ``One location`` depth
   *  means a location under that one org. */
  boundOrganizationId?: Uuid;
  boundOrganizationName?: string;
  /** Pin the picker to one partner. The partner row renders as a
   *  read-only badge - operators can't pick a different partner.
   *  The org dropdown still works (showing orgs under this partner)
   *  unless ``boundOrganizationId`` is also set.
   *
   *  Used by the InvitePersonDialog role-scope picker when the
   *  anchor is partner-scoped: the role MUST land somewhere inside
   *  the chosen partner's subtree, not on a sibling partner. */
  boundPartnerId?: Uuid;
  boundPartnerName?: string;
  className?: string;
  /** Optional id prefix so multiple instances on a page stay
   *  associatable from a label/for. */
  idPrefix?: string;
}

const ALL_KINDS: readonly ScopeKind[] = ["partner", "organization", "location"];

export function WorkplacePicker(props: WorkplacePickerProps) {
  const allowed = props.allowedKinds ?? ALL_KINDS;

  // Internal selection state - kept separate from `props.value` so
  // partial selections (partner picked, no org yet) don't leak out as
  // a "ready" value. Only when the user lands on a leaf that matches
  // an allowed kind do we call onChange with a non-empty id.
  const [partnerId, setPartnerId] = useState(props.boundPartnerId ?? "");
  const [organizationId, setOrganizationId] = useState(props.boundOrganizationId ?? "");
  const [locationId, setLocationId] = useState("");
  const [pickedKind, setPickedKind] = useState<ScopeKind | null>(
    allowed.includes("location") ? "location" : (allowed[0] ?? null),
  );

  // Skip the partner query when EITHER bound flavour pins us to a
  // single partner (boundPartnerId) or organization (boundOrganizationId,
  // which implies a partner one level up that the operator can't
  // change). The partner row renders as a read-only badge in those
  // cases; no need for the dropdown's full data set.
  const partners = useQuery({
    queryKey: ["partners", "picker", "all"],
    queryFn: () => apiClient.listPartners({ limit: 200 }),
    enabled: !props.boundOrganizationId && !props.boundPartnerId,
  });

  const partnerOptions = useMemo(() => {
    const rows = partners.data?.partners ?? [];
    // Platform is NEVER a partner option here. It's the abstract root
    // in our tenancy model - not an operator-facing workplace. Inviting
    // a platform-tier person goes through a separate top-level toggle
    // in InvitePersonDialog so the architectural distinction is
    // visible in the UI rather than buried inside the partner dropdown.
    const realPartners = rows.filter((p) => p.partner_type !== "platform");
    const filtered = props.boundPartnerPath
      ? realPartners.filter(
          (p) =>
            p.path === props.boundPartnerPath || p.path.startsWith(props.boundPartnerPath + "."),
        )
      : realPartners;
    const sorted = filtered.slice().sort((a, b) => a.path.localeCompare(b.path));

    // Sub-partners must be visually distinguishable from their parents
    // in the dropdown. A flat list of names hides the hierarchy: a
    // partner named "Metro East" reads identically whether it's a
    // top-level partner or a sub-partner under "Honda Group".
    //
    // We build a path → name lookup off this same set, then render
    // each option as "Parent › Self" (chevron joiner) for sub-partners,
    // and just "Self" for top-level. The chevron + parent name lets
    // operators tell sub-partners apart at a glance.
    const nameByPath = new Map<string, string>();
    for (const p of sorted) nameByPath.set(p.path, p.name);

    return sorted.map((p) => {
      // ltree path looks like ``platform.finterra.honda-group``. The
      // platform prefix is implicit (we exclude that partner above);
      // every chunk after it is a real partner. Depth = chunks - 1.
      const chunks = p.path.split(".");
      const ancestors = chunks.slice(1, -1); // chunks BETWEEN platform and self
      const ancestorNames: string[] = [];
      let walkPath = "platform";
      for (const slug of ancestors) {
        walkPath = walkPath ? `${walkPath}.${slug}` : slug;
        ancestorNames.push(nameByPath.get(walkPath) ?? slug);
      }
      const indent = "  ".repeat(ancestors.length); // nbsp pairs
      const display = ancestorNames.length
        ? `${indent}${ancestorNames.join(" › ")} › ${p.name}`
        : p.name;
      return { ...p, _display: display };
    });
  }, [partners.data, props.boundPartnerPath]);

  // Orgs are fetched ONLY when the user is drilling down through a
  // partner anchor (orgs don't nest, so a bound org id has no
  // 'children' to enumerate). Skipping this query is what stops the
  // "No organizations under this partner" copy from rendering when
  // the picker is pinned to one org.
  const orgs = useQuery({
    queryKey: ["organizations", "picker", partnerId],
    queryFn: () => apiClient.listOrganizations({ partner_id: partnerId, limit: 200 }),
    enabled: !props.boundOrganizationId && !!partnerId && pickedKind !== "partner",
  });

  // For role-scope work inside an org anchor we hit the locations
  // endpoint directly with the bound id; partnerId stays empty in
  // that flow.
  const locationsOrgId = props.boundOrganizationId || organizationId;
  const locations = useQuery({
    queryKey: ["locations", "picker", locationsOrgId],
    queryFn: () =>
      apiClient.listLocations({
        organization_id: locationsOrgId,
        limit: 200,
      }),
    enabled: !!locationsOrgId && pickedKind === "location",
  });

  const selectedPartner = useMemo(() => {
    if (props.boundPartnerId) {
      return {
        id: props.boundPartnerId,
        name: props.boundPartnerName ?? props.boundPartnerId,
      };
    }
    return partnerOptions.find((p) => p.id === partnerId) ?? null;
  }, [partnerOptions, partnerId, props.boundPartnerId, props.boundPartnerName]);
  const selectedOrg = useMemo(() => {
    if (props.boundOrganizationId) {
      return {
        id: props.boundOrganizationId,
        name: props.boundOrganizationName ?? props.boundOrganizationId,
      };
    }
    return (orgs.data?.organizations ?? []).find((o) => o.id === organizationId) ?? null;
  }, [orgs.data, organizationId, props.boundOrganizationId, props.boundOrganizationName]);
  const selectedLoc = useMemo(
    () => (locations.data?.locations ?? []).find((l) => l.id === locationId) ?? null,
    [locations.data, locationId],
  );

  // When partner changes, drop the org/loc selection (they belonged to
  // the previous subtree). Same for org → loc. Skip when bound to a
  // single org since we never change partnerId in that flow.
  useEffect(() => {
    if (props.boundOrganizationId) return;
    setOrganizationId("");
    setLocationId("");
  }, [partnerId, props.boundOrganizationId]);

  // Pin the internal partnerId to the bound id whenever the parent
  // supplies one. Same trick as boundOrganizationId - without this
  // the bound badge renders but downstream queries (orgs under
  // partner) never fire.
  useEffect(() => {
    if (props.boundPartnerId && partnerId !== props.boundPartnerId) {
      setPartnerId(props.boundPartnerId);
    }
  }, [props.boundPartnerId, partnerId]);

  useEffect(() => {
    setLocationId("");
  }, [organizationId]);

  // Pin the internal organizationId to the bound id whenever the
  // parent supplies one. Without this, "Organization" depth would
  // emit an empty value (Submit stays disabled) because the user
  // never opens a dropdown that's wired to the bound id.
  useEffect(() => {
    if (props.boundOrganizationId && organizationId !== props.boundOrganizationId) {
      setOrganizationId(props.boundOrganizationId);
    }
  }, [props.boundOrganizationId, organizationId]);

  // If `allowedKinds` shrinks under our feet, rescue the picked depth.
  useEffect(() => {
    if (pickedKind && !allowed.includes(pickedKind)) {
      setPickedKind(allowed[0] ?? null);
    }
  }, [allowed, pickedKind]);

  // Materialise the value and bubble up. We compute eagerly on every
  // render so the parent's submit-disabled gate stays in sync.
  useEffect(() => {
    let id = "";
    if (pickedKind === "partner") id = partnerId;
    else if (pickedKind === "organization") id = organizationId;
    else if (pickedKind === "location") id = locationId;
    const next: WorkplacePickerValue = {
      kind: pickedKind,
      id,
      labels: {
        partner: selectedPartner?.name ?? null,
        organization: selectedOrg?.name ?? null,
        location: selectedLoc?.name ?? null,
      },
    };
    if (
      next.kind !== props.value.kind ||
      next.id !== props.value.id ||
      next.labels.partner !== props.value.labels.partner ||
      next.labels.organization !== props.value.labels.organization ||
      next.labels.location !== props.value.labels.location
    ) {
      props.onChange(next);
    }
  }, [
    pickedKind,
    partnerId,
    organizationId,
    locationId,
    selectedPartner,
    selectedOrg,
    selectedLoc,
    props,
  ]);

  const idPrefix = props.idPrefix ?? "wp";
  const showPartner = !props.boundOrganizationId;
  const partnerLabel = props.boundPartnerPath ? "Partner (in scope)" : "Partner";

  return (
    <div className={cn("space-y-3", props.className)}>
      {/* Depth tabs - which level we're granting at. */}
      {allowed.length > 1 && (
        <div className="flex gap-2" role="tablist" aria-label="Scope depth">
          {allowed.map((k) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={pickedKind === k}
              onClick={() => setPickedKind(k)}
              className={cn(
                "rounded-md border px-2 py-1 text-xs font-medium",
                pickedKind === k
                  ? "border-slate-900 bg-slate-900 text-white"
                  : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50",
              )}
            >
              {labelForKind(k)}
            </button>
          ))}
        </div>
      )}

      {/* Partner row.
          Bound case: render the bound partner as a read-only badge -
          the operator can't change it. Free case: full dropdown.
          Hidden entirely when boundOrganizationId is set since the
          partner above the bound org is implicit. */}
      {showPartner && props.boundPartnerId ? (
        <div className="space-y-1">
          <div className="text-xs font-medium text-slate-600">Partner</div>
          <div className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
            <span className="font-medium">{props.boundPartnerName ?? props.boundPartnerId}</span>
            <span className="text-[10px] uppercase tracking-wide text-slate-400">pinned</span>
          </div>
        </div>
      ) : showPartner ? (
        <div className="space-y-1">
          <label htmlFor={`${idPrefix}-partner`} className="text-xs font-medium text-slate-600">
            {partnerLabel}
          </label>
          <select
            id={`${idPrefix}-partner`}
            value={partnerId}
            onChange={(e) => setPartnerId(e.target.value)}
            disabled={partners.isLoading}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          >
            <option value="">
              {partners.isLoading
                ? "Loading partners…"
                : partnerOptions.length === 0
                  ? "No partners in scope"
                  : "Select a partner…"}
            </option>
            {partnerOptions.map((p) => (
              <option key={p.id} value={p.id}>
                {p._display}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      {/* Organization row.
          Bound case: render the bound org as a read-only badge - orgs
          don't contain other orgs, so there's nothing for the user to
          pick under it. Free case: dropdown of orgs under the picked
          partner. */}
      {pickedKind !== "partner" && props.boundOrganizationId ? (
        <div className="space-y-1">
          <div className="text-xs font-medium text-slate-600">Organization</div>
          <div className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
            <span className="font-medium">
              {props.boundOrganizationName ?? props.boundOrganizationId}
            </span>
            <span className="text-[10px] uppercase tracking-wide text-slate-400">pinned</span>
          </div>
        </div>
      ) : pickedKind !== "partner" && partnerId ? (
        <div className="space-y-1">
          <label htmlFor={`${idPrefix}-org`} className="text-xs font-medium text-slate-600">
            Organization
          </label>
          <select
            id={`${idPrefix}-org`}
            value={organizationId}
            onChange={(e) => setOrganizationId(e.target.value)}
            disabled={orgs.isLoading}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          >
            <option value="">
              {orgs.isLoading
                ? "Loading organizations…"
                : (orgs.data?.organizations ?? []).length === 0
                  ? "No organizations under this partner"
                  : "Select an organization…"}
            </option>
            {(orgs.data?.organizations ?? []).map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      {/* Location row - gated on org (free or bound) */}
      {pickedKind === "location" && (organizationId || props.boundOrganizationId) && (
        <div className="space-y-1">
          <label htmlFor={`${idPrefix}-loc`} className="text-xs font-medium text-slate-600">
            Location
          </label>
          <select
            id={`${idPrefix}-loc`}
            value={locationId}
            onChange={(e) => setLocationId(e.target.value)}
            disabled={locations.isLoading}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          >
            <option value="">
              {locations.isLoading
                ? "Loading locations…"
                : (locations.data?.locations ?? []).length === 0
                  ? "No locations under this organization"
                  : "Select a location…"}
            </option>
            {(locations.data?.locations ?? []).map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}

function labelForKind(kind: ScopeKind): string {
  switch (kind) {
    case "partner":
      return "Partner";
    case "organization":
      return "Organization";
    case "location":
      return "Location";
  }
}
